import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import {
  formatKES,
  currentMonth,
  utilityChargeForMonth,
  utilityBalanceForMonth,
} from "../utils/format";
import { MessageSquare, Loader2, Info, CheckCircle2, XCircle } from "lucide-react";

const TEMPLATES = {
  water_unpaid:
    "Hi {name}, your water bill of {waterAmount} for house {house} is outstanding. Balance: {waterBalance}. Please arrange payment. Thank you.",
  electricity_unpaid:
    "Hi {name}, your electricity bill of {electricityAmount} for house {house} is outstanding. Balance: {electricityBalance}. Please arrange payment. Thank you.",
  all_unpaid:
    "Hi {name}, your utility bills for house {house} are outstanding: water ({waterBalance}) and electricity ({electricityBalance}). Total: {utilityTotal}. Please arrange payment. Thank you.",
  all_with_bills:
    "Hi {name}, your utility bills for house {house} this month are: water {waterAmount}, electricity {electricityAmount}. Please ensure timely payment. Thank you.",
};

function renderMessage(template, tenant, payments, utilityCharges, month) {
  const wc = utilityChargeForMonth(utilityCharges, tenant.id, month, "water");
  const wb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "water");
  const ec = utilityChargeForMonth(utilityCharges, tenant.id, month, "electricity");
  const eb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "electricity");
  return String(template)
    .replaceAll("{name}", tenant.name || "")
    .replaceAll("{house}", tenant.houseNumber || "")
    .replaceAll("{waterAmount}", formatKES(wc))
    .replaceAll("{waterBalance}", formatKES(wb))
    .replaceAll("{electricityAmount}", formatKES(ec))
    .replaceAll("{electricityBalance}", formatKES(eb))
    .replaceAll("{utilityTotal}", formatKES(wb + eb));
}

export default function BulkUtilitySmsDialog({ open, onClose, tenants = [] }) {
  const { payments, utilityCharges, sendBulkSms } = useData();
  const month = currentMonth();
  const [template, setTemplate] = useState(TEMPLATES.all_unpaid);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState("all_unpaid");
  const [excluded, setExcluded] = useState(new Set());
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setTarget("all_unpaid");
      setTemplate(TEMPLATES.all_unpaid);
      setExcluded(new Set());
      setResult(null);
    }
  }, [open]);

  const changeTarget = (next) => {
    setTarget(next);
    setExcluded(new Set());
    const isDefault = Object.values(TEMPLATES).includes(template);
    if (isDefault) setTemplate(TEMPLATES[next] || TEMPLATES.all_unpaid);
  };

  const recipients = useMemo(() => {
    return tenants
      .filter((t) => t.status === "ACTIVE")
      .filter((t) => {
        const wc = utilityChargeForMonth(utilityCharges, t.id, month, "water");
        const wb = utilityBalanceForMonth(utilityCharges, payments, t.id, month, "water");
        const ec = utilityChargeForMonth(utilityCharges, t.id, month, "electricity");
        const eb = utilityBalanceForMonth(utilityCharges, payments, t.id, month, "electricity");

        switch (target) {
          case "water_unpaid":
            return wc > 0 && wb > 0;
          case "electricity_unpaid":
            return ec > 0 && eb > 0;
          case "all_unpaid":
            return (wc > 0 && wb > 0) || (ec > 0 && eb > 0);
          case "all_with_bills":
            return wc > 0 || ec > 0;
          default:
            return false;
        }
      });
  }, [tenants, target, payments, utilityCharges, month]);

  const countsByTarget = useMemo(() => {
    const active = tenants.filter((t) => t.status === "ACTIVE");
    const out = { water_unpaid: 0, electricity_unpaid: 0, all_unpaid: 0, all_with_bills: 0 };
    active.forEach((t) => {
      const wc = utilityChargeForMonth(utilityCharges, t.id, month, "water");
      const wb = utilityBalanceForMonth(utilityCharges, payments, t.id, month, "water");
      const ec = utilityChargeForMonth(utilityCharges, t.id, month, "electricity");
      const eb = utilityBalanceForMonth(utilityCharges, payments, t.id, month, "electricity");
      if (wc > 0 && wb > 0) out.water_unpaid++;
      if (ec > 0 && eb > 0) out.electricity_unpaid++;
      if ((wc > 0 && wb > 0) || (ec > 0 && eb > 0)) out.all_unpaid++;
      if (wc > 0 || ec > 0) out.all_with_bills++;
    });
    return out;
  }, [tenants, payments, utilityCharges, month]);

  const selected = recipients.filter((t) => !excluded.has(t.id));

  const toggleOne = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (excluded.size === 0) {
      setExcluded(new Set(recipients.map((t) => t.id)));
    } else {
      setExcluded(new Set());
    }
  };

  const preview = selected[0]
    ? renderMessage(template, selected[0], payments, utilityCharges, month)
    : template;

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0 || !template.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const messages = selected.map((t) => ({
        tenantId: t.id,
        message: renderMessage(template, t, payments, utilityCharges, month),
      }));
      const data = await sendBulkSms(messages);
      const queued = data.queued != null ? data.queued : (data.communications?.length || messages.length);
      setResult({ queued, failed: 0, total: data.total || queued });
    } catch (err) {
      console.error("Bulk utility SMS failed:", err);
      setResult({ queued: 0, failed: selected.length, total: selected.length });
    }
    setLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk utility SMS"
      description={result ? "Delivery summary" : `Send utility reminders to ${selected.length} tenant${selected.length === 1 ? "" : "s"}.`}
    >
      {result ? (
        <div className="space-y-4">
          {result.failed === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                  {result.queued} message{result.queued === 1 ? "" : "s"} queued for delivery
                </p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  Messages are being sent in the background. You'll see them update in each tenant's history as they're delivered.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
              <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">SMS sending failed</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  Could not queue messages for {result.total} recipient{result.total === 1 ? "" : "s"}. Please try again.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>Done</button>
          </div>
        </div>
      ) : (
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label-base">Target</label>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "all_unpaid", l: "All unpaid", c: countsByTarget.all_unpaid },
              { v: "water_unpaid", l: "Water unpaid", c: countsByTarget.water_unpaid },
              { v: "electricity_unpaid", l: "Elec unpaid", c: countsByTarget.electricity_unpaid },
              { v: "all_with_bills", l: "All with bills", c: countsByTarget.all_with_bills },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => changeTarget(o.v)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                  target === o.v
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {o.l} · {o.c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-base">Message template</label>
          <textarea
            className="input-base min-h-[110px] resize-y font-mono text-xs"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            required
          />
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <Info className="mt-px h-3 w-3 flex-none" />
            <span>
              Placeholders: <code>{"{name}"}</code>, <code>{"{house}"}</code>,{" "}
              <code>{"{waterAmount}"}</code>, <code>{"{waterBalance}"}</code>,{" "}
              <code>{"{electricityAmount}"}</code>, <code>{"{electricityBalance}"}</code>,{" "}
              <code>{"{utilityTotal}"}</code>
            </span>
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Preview
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-900 dark:text-white">
            {recipients.length === 0 ? (
              <span className="text-gray-400">No recipients match this target.</span>
            ) : (
              preview
            )}
          </div>
        </div>

        {recipients.length > 0 && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Recipients ({selected.length} of {recipients.length})
              </span>
              <button type="button" onClick={toggleAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                {excluded.size === 0 ? "Deselect all" : "Select all"}
              </button>
            </div>
            <ul className="max-h-40 divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              {recipients.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => toggleOne(t.id)}
                >
                  <input
                    type="checkbox"
                    checked={!excluded.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    className="checkbox-base"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">
                    {t.name}
                    {t.houseNumber && (
                      <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">· House {t.houseNumber}</span>
                    )}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">{t.phone || "no phone"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || selected.length === 0}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquare className="h-4 w-4" />
            )}
            Send {selected.length} SMS
          </button>
        </div>
      </form>
      )}
    </Modal>
  );
}
